import { useWinWatcher } from '@/hooks/use-win-watcher'
import { useAccountEvents } from '@/hooks/use-account-events'
import { useState } from 'react'
import { Link, NavLink, Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom'
import { useGameInfoAutocomplete, useLogout, useMe } from '@/hooks/queries'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useArrival } from '@/hooks/use-arrival'
import { useDismissable } from '@/hooks/use-dismissable'
import { useAuthStore } from '@/store/auth'
import { useOverlayStore } from '@/store/overlays'
import { useNotificationStore } from '@/store/notifications'
import { useSearchStore } from '@/store/search'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import { Overlay } from '@/components/ui/Overlay'
import { Auth } from '@/components/Auth'
import { GiveawayNew } from '@/components/giveaway-new/GiveawayNew'
import { GiveawayDetails } from '@/components/GiveawayDetails'
import { GiveawayEditForm } from '@/components/GiveawayEdit'

/**
 * Primary navigation, in the bar rather than buried in the avatar menu. The
 * Quasar app had no visible navigation at all — the whole toolbar was
 * commented out — and the first React pass put these behind the avatar, which
 * hides the app's own sections behind a control that reads as "account".
 */
const PRIMARY_LINKS = [
  { to: '/', label: 'Home', end: true, requiresAuth: false },
  { to: '/my/giveaways', label: 'My Giveaways', end: false, requiresAuth: true },
  { to: '/my/loot', label: 'My Loot', end: false, requiresAuth: true },
  { to: '/my/media', label: 'My Media', end: false, requiresAuth: true },
] as const

/**
 * Port of layouts/MainLayout.vue.
 *
 * The Quasar file had its entire toolbar commented out and, on mount, forced
 * either the Auth overlay (signed out) or My Loot (signed in) — the app had
 * been trimmed to a single-purpose key hand-out. The toolbar is rebuilt from
 * that commented markup; the forced overlay is not.
 *
 * Only the three transient flows are still overlays: signing in, composing a
 * giveaway, and the quick look at one. The account screens are pages.
 */
/** A giveaway's own page, the link people share. */
const isGiveawayPage = (pathname: string) =>
  matchPath('/g/:slug', pathname) !== null || matchPath('/giveaway/:idOrSlug', pathname) !== null

export function MainLayout() {
  const navigate = useNavigate()
  const location = useLocation()

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const { data: me } = useMe()
  useWinWatcher()
  useAccountEvents()
  const unreadNotifications = useNotificationStore((state) => state.unread)
  const notificationArriving = useArrival(unreadNotifications)
  const logout = useLogout()

  const overlays = useOverlayStore()
  const { query, setQuery, clear: clearSearch } = useSearchStore()

  // Signed-out visitors on a shared giveaway link see just the giveaway, no
  // site navigation; the page's own Enter button is how they sign in.
  const showToolbar = isAuthenticated || !isGiveawayPage(location.pathname)

  const debouncedQuery = useDebouncedValue(query, 250)
  const { data: autocomplete = [] } = useGameInfoAutocomplete(debouncedQuery)
  const [searchFocused, setSearchFocused] = useState(false)

  const avatarMenu = useDismissable<HTMLDivElement>()

  const goHome = () => {
    if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' })
    else navigate('/')
  }

  const submitSearch = () => {
    setSearchFocused(false)
    if (query.trim().length > 0) navigate(`/search?q=${encodeURIComponent(query.trim())}`)
  }

  return (
    <div className="app-layout">
      {showToolbar ? (
        <header className="toolbar">
          <div className="logo-section">
            <button type="button" className="logo-btn" aria-label="Home" onClick={goHome}>
              <img src="/icons/logo.png" width={46} alt="" />
            </button>

            <nav className="toolbar-nav" aria-label="Primary">
              {PRIMARY_LINKS.filter((link) => !link.requiresAuth || isAuthenticated).map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) => cn('toolbar-nav-link', isActive && 'active')}
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="search-section">
            <div className="search-box">
              <input
                placeholder="Search giveaways"
                aria-label="Search giveaways"
                className="search-input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => window.setTimeout(() => setSearchFocused(false), 150)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    submitSearch()
                  }
                  if (event.key === 'Escape') clearSearch()
                }}
              />
              <button
                type="button"
                className="search-btn"
                aria-label="Search"
                onClick={submitSearch}
              >
                <img className="search-icon" src="/icons/search-icon.png" alt="" />
              </button>

              {searchFocused && autocomplete.length > 0 ? (
                <div className="search-dropdown">
                  {autocomplete.map((result) => (
                    <button
                      type="button"
                      className="search-dropdown-item"
                      key={result.id}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        setQuery(result.name ?? '')
                        navigate(`/search?q=${encodeURIComponent(result.name ?? '')}`)
                        setSearchFocused(false)
                      }}
                    >
                      <div
                        className="search-dropdown-item-cover"
                        style={backgroundImage(result.display_cover_thumbnail)}
                      />
                      <div className="search-dropdown-item-title">{result.name}</div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="nav-section">
            {!isAuthenticated ? (
              <button type="button" className="login-link" onClick={() => overlays.open('auth')}>
                Login
              </button>
            ) : (
              <div className="nav-row">
                <div className="navs">
                  {/* A link, not a dropdown. The dropdown only ever held a
                      placeholder, and the messages it would list are worth a page
                      you can scroll, copy from and return to. */}
                  <Link
                    className={cn(
                      'nav-icon',
                      unreadNotifications > 0 && 'has-unread',
                      notificationArriving && 'arriving',
                    )}
                    to="/notifications"
                    aria-label={
                      unreadNotifications > 0
                        ? `Notifications, ${unreadNotifications} unread`
                        : 'Notifications'
                    }
                  >
                    <img src="/icons/notification_icon.png" alt="" />
                  </Link>

                  <button
                    type="button"
                    className="nav-icon"
                    aria-label="Create a giveaway"
                    onClick={() => overlays.open('new-giveaway')}
                  >
                    <img src="/icons/new_icon.png" alt="" />
                  </button>
                </div>

                <Link className="loot-points" to="/my/loot" title="Your loot points">
                  <div className="logo">
                    <img src="/icons/logo.png" alt="" />
                  </div>
                  <div className="points">{(me?.display_loot_points ?? 0).toLocaleString()}</div>
                </Link>

                <div className="dropdown" ref={avatarMenu.ref}>
                  <button
                    type="button"
                    className="nav avatar"
                    aria-label="Account menu"
                    aria-expanded={avatarMenu.isOpen}
                    style={backgroundImage(me?.display_profile_picture.src)}
                    onClick={avatarMenu.toggle}
                  />
                  <div className={cn('nav-dropdown right', avatarMenu.isOpen && 'show')}>
                    {me?.username ? (
                      <Link
                        className="nav-dropdown-section highlight dropdown-button"
                        to={`/u/${me.username}`}
                        onClick={avatarMenu.close}
                      >
                        {me.display_name}
                      </Link>
                    ) : (
                      <div className="nav-dropdown-section highlight">{me?.display_name}</div>
                    )}

                    <Link
                      className="nav-dropdown-section dropdown-button"
                      to="/my/profile"
                      onClick={avatarMenu.close}
                    >
                      My Profile
                    </Link>

                    <button
                      type="button"
                      className="nav-dropdown-section dropdown-button"
                      onClick={() => {
                        avatarMenu.close()
                        overlays.closeAll()
                        logout()
                        navigate('/')
                      }}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>
      ) : null}

      <main className="app-page">
        <Outlet />
      </main>

      {/* Every overlay stays mounted through its exit animation, so closing
          one settles rather than blinking out. */}
      <Overlay
        name="auth"
        label="Sign in"
        open={overlays.isOpen('auth')}
        onClose={() => overlays.close('auth')}
      >
        <Auth onClose={() => overlays.close('auth')} />
      </Overlay>

      <Overlay
        name="new-giveaway"
        label="Create a giveaway"
        open={overlays.isOpen('new-giveaway')}
        onClose={() => overlays.close('new-giveaway')}
      >
        <GiveawayNew
          initialGiveaway={overlays.draftGiveaway}
          onClose={() => {
            overlays.setDraftGiveaway(null)
            overlays.close('new-giveaway')
          }}
        />
      </Overlay>

      <Overlay
        name="giveaway-details"
        label={overlays.selectedGiveaway?.name ?? 'Giveaway'}
        open={overlays.isOpen('giveaway-details') && overlays.selectedGiveaway !== null}
        onClose={() => overlays.close('giveaway-details')}
      >
        {overlays.selectedGiveaway ? (
          <GiveawayDetails
            giveaway={overlays.selectedGiveaway}
            onClose={() => overlays.close('giveaway-details')}
          />
        ) : null}
      </Overlay>

      <Overlay
        name="edit-giveaway"
        label="Edit giveaway"
        open={overlays.isOpen('edit-giveaway') && overlays.editingGiveaway !== null}
        onClose={() => overlays.close('edit-giveaway')}
      >
        {overlays.editingGiveaway ? (
          <GiveawayEditForm
            giveaway={overlays.editingGiveaway}
            followSlugChange={location.pathname.startsWith('/g/')}
            onClose={() => overlays.close('edit-giveaway')}
          />
        ) : null}
      </Overlay>
    </div>
  )
}
